import type { ConversationAccessMode } from "@/api/chat/chat.types";

import { useConversationDetail } from "./useConversationDetail";

export type AccessMode = ConversationAccessMode | "loading" | "error";

/**
 * Collapse the ConversationDetail query into a single discriminated state
 * so every owner-gated control on the chat page branches on one value
 * instead of juggling `isPending` + `data?.access_mode`.
 *
 * The `"loading"` tier is the flash-of-owner-controls guard: until we
 * know who the viewer is, callers must treat the mode as non-owner.
 * Rendering owner controls optimistically and hiding them on resolve
 * leaks those affordances to collaborators on slow networks.
 *
 * The `"error"` tier preserves the same fail-closed posture for any
 * caller comparing against `"owner"`, but lets observability layers
 * distinguish a settled error from an in-flight request — previously
 * both reported as `"loading"` and a permanent failure showed as a
 * never-resolving spinner.
 */
export function useAccessMode(
    remoteId: number | string | undefined
): AccessMode {
    const { data, isError } = useConversationDetail(remoteId);

    // Fallback to "loading" if the response shape is missing access_mode —
    // TS guarantees the field, but a malformed body shouldn't unlock owner
    // controls on a `?? "owner"`-style mistake downstream.
    if (data) return data.access_mode ?? "loading";
    if (isError) return "error";
    return "loading";
}
